import React, { useEffect, useRef, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { BookOpen, BookOpenText, Users } from 'lucide-react';

import ClassesManagement from './ClassesManagement';
import MatieresManagement from './MatieresManagement';
import GuideUtilisationDrawer from './GuideUtilisationDrawer';
import { useDroitsClassesMatieres } from './droitsClassesMatieres';

const ClassesMatieres = () => {
  const [selectedMainTab, setSelectedMainTab] = useState('classes');
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // Les droits d'écriture sont lus une seule fois pour les deux onglets, qui
  // restent montés, puis relus à chaque changement d'onglet et après chaque
  // refus du serveur : l'écran suit une permission accordée ou retirée sans
  // qu'on recharge la page.
  const { droits, erreur: erreurDroits, recharger: rechargerDroits } = useDroitsClassesMatieres();
  const ongletPrecedentRef = useRef(selectedMainTab);
  useEffect(() => {
    if (ongletPrecedentRef.current === selectedMainTab) return;
    ongletPrecedentRef.current = selectedMainTab;
    rechargerDroits();
  }, [selectedMainTab, rechargerDroits]);

  return (
    <div className="w-full p-4">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-[#246990]">Classes & Matières</h1>
        <button
          type="button"
          onClick={() => setIsGuideOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#246990]/20 bg-[#246990]/10 px-3 py-2 text-sm font-semibold text-[#246990] transition hover:bg-[#246990]/15"
        >
          <BookOpenText size={16} />
          Guide d'utilisation
        </button>
      </div>

      {erreurDroits && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <span className="min-w-0 flex-1">
            Vos droits sur les classes n'ont pas pu être vérifiés : les modifications sont masquées.
          </span>
          <button
            type="button"
            onClick={rechargerDroits}
            className="h-8 shrink-0 rounded-lg border border-amber-300 px-3 text-xs font-semibold transition-colors hover:bg-amber-100"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Navigation principale avec navigation stable */}
      <Tabs
        defaultValue="classes"
        value={selectedMainTab}
        onValueChange={setSelectedMainTab}
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 mb-8 bg-[#246990]/10 w-full">
          <TabsTrigger
            value="classes"
            className="flex items-center justify-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white min-w-0"
          >
            <BookOpen size={16} />
            <span className="hidden md:inline whitespace-nowrap">Classes</span>
          </TabsTrigger>

          <TabsTrigger
            value="matieres"
            className="flex items-center justify-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white min-w-0"
          >
            <Users size={16} />
            <span className="hidden md:inline whitespace-nowrap">Matières</span>
          </TabsTrigger>
        </TabsList>

        {/* Contenu des onglets : les deux restent montés (forceMount) et l'onglet
            inactif est seulement masqué. Sans cela, chaque changement d'onglet
            démontait l'autre, qui rechargeait tout et repassait par la roue de
            chargement. L'onglet qui revient se rafraîchit en arrière-plan. */}
        <TabsContent value="classes" forceMount className="w-full data-[state=inactive]:hidden">
          <ClassesManagement
            estActif={selectedMainTab === 'classes'}
            droits={droits}
            onRefusDroit={rechargerDroits}
          />
        </TabsContent>

        <TabsContent value="matieres" forceMount className="w-full data-[state=inactive]:hidden">
          <MatieresManagement
            estActif={selectedMainTab === 'matieres'}
            onOpenClassesTab={() => setSelectedMainTab('classes')}
            droits={droits}
            onRefusDroit={rechargerDroits}
          />
        </TabsContent>
      </Tabs>
      <GuideUtilisationDrawer
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        module="classesMatieres"
      />
    </div>
  );
};

export default ClassesMatieres;